/**
 * Flight Refund Ecommerce Payload Builder
 * Uçuş iptali (refund) dataLayer event'i için ecommerce objesi oluşturur.
 * Bus refund pattern'i ile uyumlu: opsiyonel alanlar sadece değer varsa eklenir.
 */

/**
 * Product.IsTurkishDomestic'tan item_category2 (domestic/international) belirler
 * @param {object} product - Basket.Products elemanı
 * @returns {string|null}
 */
const getItemCategory2FromProduct = (product) =>
  product?.IsTurkishDomestic === true
    ? "domestic"
    : product?.IsTurkishDomestic === false
      ? "international"
      : null;

/**
 * selectedSegmentType'a göre dahil edilecek leg index'lerini döner
 * @param {string} selectedSegmentType - "Departure" | "Return" | "All"
 * @param {number} totalLegs
 * @returns {number[]}
 */
const getLegIndexesForSegment = (selectedSegmentType, totalLegs) => {
  if (!selectedSegmentType || totalLegs === 0) return [];
  if (selectedSegmentType === "All") {
    return Array.from({ length: totalLegs }, (_, i) => i);
  }
  if (selectedSegmentType === "Departure" && totalLegs >= 1) return [0];
  if (selectedSegmentType === "Return" && totalLegs >= 2) return [1];
  if (selectedSegmentType === "MultiCity") {
    return Array.from({ length: totalLegs }, (_, i) => i);
  }
  return [0];
};

/**
 * Uçuş iptali (refund) ecommerce objesi oluşturur.
 * getBasketByHashCode response'undan türetilir.
 *
 * @param {Object} params
 * @param {Array} params.products - Basket.Products (flight)
 * @param {Object} params.basketData - Data.Basket veya Data
 * @param {Object} params.priceSummary - Basket.PriceSummary veya TotalPriceSummary.PriceSummary
 * @param {string} [params.selectedSegmentType] - "Departure" | "Return" | "All"
 * @param {Array} [params.selectedPassengerIds] - İptal edilen yolcu ID'leri
 * @param {string} [params.currency="TRY"]
 * @returns {Object|null} ecommerce objesi veya geçersizse null
 */
export const buildFlightRefundEcommerce = ({
  products = [],
  basketData = null,
  priceSummary = null,
  selectedSegmentType = "All",
  selectedPassengerIds = [],
  currency = "TRY",
}) => {
  if (!Array.isArray(products) || products.length === 0) {
    return null;
  }

  const legsWithProduct = products.flatMap((p) =>
    (p?.Legs ?? p?.legs ?? [])
      .filter(Boolean)
      .map((leg) => ({ leg, product: p })),
  );
  if (legsWithProduct.length === 0) return null;

  const legIndexes = getLegIndexesForSegment(
    selectedSegmentType,
    legsWithProduct.length,
  );
  const legsToInclude = legIndexes
    .map((i) => legsWithProduct[i])
    .filter(Boolean);
  if (legsToInclude.length === 0) return null;

  const summary = priceSummary ?? products[0]?.PriceSummary ?? {};
  const totalFare =
    Number(
      summary?.TotalFare ?? summary?.SaleBaseFare ?? summary?.BaseFare ?? 0,
    ) || 0;
  const currencyCode = summary?.Currency ?? currency?.toUpperCase() ?? "TRY";

  const value =
    legsToInclude.length === legsWithProduct.length
      ? totalFare
      : Math.round(
          (totalFare / legsWithProduct.length) * legsToInclude.length * 100,
        ) / 100;
  const pricePerItem =
    legsToInclude.length > 0
      ? Math.round((value / legsToInclude.length) * 100) / 100
      : 0;

  const basket = basketData?.Basket ?? basketData ?? {};
  const basketId =
    basket?.BasketId ?? basketData?.BasketId ?? basketData?.Basket?.BasketId;
  const hasBasketId =
    basketId != null &&
    String(basketId).trim() !== "" &&
    (Number.isFinite(Number(basketId)) || String(basketId).trim() !== "");

  const rawAllPayments = basket?.AllPayments ?? basketData?.AllPayments ?? [];
  const allPayments = Array.isArray(rawAllPayments) ? rawAllPayments : [];
  const finalizedPayment =
    allPayments.find((p) => p?.IsFinalized === true) ?? allPayments[0] ?? null;

  const coupon =
    basket?.UsedCampaign?.Code ??
    basket?.UsedCampaign?.Title ??
    basket?.UsedCampaign?.Name ??
    basketData?.UsedCampaign?.Code ??
    basketData?.UsedCampaign?.Title ??
    "";

  const creditCardType = finalizedPayment?.CardOrganizationType ?? null;
  const creditCardBank = finalizedPayment?.BankName ?? null;
  const paymentInstallment = finalizedPayment?.InstallmentCount ?? null;

  const items = legsToInclude.map(({ leg, product }, idx) => {
    const segments = leg?.Segments ?? leg?.segments ?? [];
    const firstSegment = segments[0] ?? null;
    const lastSegment = segments[segments.length - 1] ?? firstSegment;
    const origin =
      firstSegment?.Origin ?? firstSegment?.OriginAirportCode ?? "";
    const destination =
      lastSegment?.Destination ?? lastSegment?.DestinationAirportCode ?? "";
    const itemId =
      `${origin}-${destination}`.replace(/\s+/g, "-").trim() || null;
    const itemBrand =
      firstSegment?.MarketingAirlineName ??
      firstSegment?.MarketingAirline ??
      null;
    const itemCategory2 = getItemCategory2FromProduct(product);

    return {
      item_id: itemId || null,
      item_name: "flight",
      affiliation: "0",
      currency: currencyCode,
      index: idx,
      item_category: "flight",
      item_category2: itemCategory2,
      item_brand: itemBrand,
      item_category3: firstSegment?.MarketingAirline || null,
      price: pricePerItem,
      quantity: 1,
    };
  });

  return {
    ...(hasBasketId && { transaction_id: String(basketId) }),
    value,
    shipping: 0,
    tax: 0,
    currency: currencyCode,
    coupon: coupon || "",
    payment_type: "credit card",
    ...(creditCardType && {
      credit_card_type: String(creditCardType).toLowerCase(),
    }),
    ...(creditCardBank && { credit_card_bank: creditCardBank }),
    ...(paymentInstallment != null && {
      payment_installment: Number(paymentInstallment),
    }),
    items,
  };
};
